import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import toast from "react-hot-toast";
import {
  getOccasionsByEventId,
  getTrackedInvites,
  allocateSeat,
  getSeatAllocation,
  updateOccasion,
} from "../../server/Api";
import { getMainGuestsAndAttendees } from "../../utils/getGuestWithAttendees";
import { fetchAllocations } from "../../utils/getAllocatedGuestAndAttendees";
import "../../assets/css/occasion.css";
import "../../assets/css/SeatAllocationBoard.css";
import {
  getSeatingStyle,
  stylesForAllocation,
  buildOccasionSeatPlan,
} from "./seatingStyles";
import { Link } from "react-router-dom";

const personName = (person = {}) =>
  [
    person.prefix,
    person.firstName,
    person.middleName || person.MiddleName,
    person.lastName,
    person.suffix,
  ]
    .filter(Boolean)
    .join(" ");

const toNum = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

const idStr = (value) => (value == null ? "" : String(value));

const sameId = (a, b) => a != null && b != null && String(a) === String(b);

const DND_TYPE = "application/x-festiva-seat-person";

const parseSeatKey = (seatKey) => {
  const row = /^row-(\d+)-(\d+)$/.exec(seatKey || "");
  if (row) {
    return { kind: "row", location: Number(row[1]), seat: Number(row[2]) };
  }
  const table = /^table-(\d+)-(\d+)$/.exec(seatKey || "");
  if (table) {
    return {
      kind: "table",
      location: Number(table[1]),
      seat: Number(table[2]),
    };
  }
  return null;
};

const seatKeyFromPlacement = (placement) => {
  if (!placement?.seat) return null;
  if (placement.row) return `row-${placement.row}-${placement.seat}`;
  if (placement.table) return `table-${placement.table}-${placement.seat}`;
  return null;
};

const seatInitial = (person) => {
  const first = String(person?.firstName || person?.name || "")
    .trim()
    .charAt(0);
  const last = String(person?.lastName || "")
    .trim()
    .charAt(0);
  if (first || last) return `${first}${last}`.toUpperCase();
  return String(person?.name || "?")
    .trim()
    .slice(0, 2)
    .toUpperCase();
};

const seatLocationLabel = (seatKey) => {
  const row = /^row-(\d+)-(\d+)$/.exec(seatKey || "");
  if (row) return `Row ${row[1]} · Seat ${row[2]}`;
  const table = /^table-(\d+)-(\d+)$/.exec(seatKey || "");
  if (table) return `Table ${table[1]} · Seat ${table[2]}`;
  return "";
};

const SeatAllocationBoard = () => {
  const selectedEvent = useSelector((state) => state.auth.selectedEvent);
  const eventEntity = useSelector((state) => state.auth.user);
  const isViewer = eventEntity?.role === "Viewer";
  const dragGhostRef = useRef(null);
  const partyLeaveTimerRef = useRef(null);
  const occasionMenuRef = useRef(null);

  const [occasions, setOccasions] = useState([]);
  const [occasionId, setOccasionId] = useState("");
  const [occasionMenuOpen, setOccasionMenuOpen] = useState(false);
  const [guestWithAttendees, setGuestWithAttendees] = useState([]);
  const [selectedAllocations, setSelectedAllocations] = useState({});
  const [rsvpMap, setRsvpMap] = useState({});
  const [savedAllocations, setSavedAllocations] = useState([]);
  const [placements, setPlacements] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState("auto");
  const [dragOverSeat, setDragOverSeat] = useState(null);
  const [draggingKey, setDraggingKey] = useState(null);
  const [listDropActive, setListDropActive] = useState(false);
  const [saving, setSaving] = useState(false);
  const [zoomByKind, setZoomByKind] = useState({ row: 1, table: 1 });
  const [hoveredPartyId, setHoveredPartyId] = useState(null);
  const [hoveredSeatKey, setHoveredSeatKey] = useState(null);
  const [pinnedPartyId, setPinnedPartyId] = useState(null);
  const [pinnedSeatKey, setPinnedSeatKey] = useState(null);
  const [partyCardPos, setPartyCardPos] = useState(null);
  const [seatingStyleId, setSeatingStyleId] = useState("classic");
  const [savingLayoutPick, setSavingLayoutPick] = useState(false);
  const [layoutPickerOpen, setLayoutPickerOpen] = useState(false);

  const occasion = useMemo(
    () => occasions.find((o) => sameId(o._id, occasionId)) || null,
    [occasions, occasionId]
  );

  const allocationType = String(occasion?.seatAllocationType || "")
    .toLowerCase()
    .trim();

  const mode =
    viewMode === "auto"
      ? allocationType === "both"
        ? "both"
        : allocationType || "row"
      : viewMode;

  const customSeatingStyles = useMemo(
    () =>
      Array.isArray(occasion?.customSeatingStyles)
        ? occasion.customSeatingStyles
        : [],
    [occasion]
  );

  const seatingStyle = useMemo(
    () => getSeatingStyle(seatingStyleId, customSeatingStyles),
    [seatingStyleId, customSeatingStyles]
  );

  const availableStyles = useMemo(
    () => stylesForAllocation(allocationType || "both", customSeatingStyles),
    [allocationType, customSeatingStyles]
  );

  const useStyledFloor =
    Boolean(seatingStyle?.isCustom) ||
    (seatingStyleId && seatingStyleId !== "classic");

  useEffect(() => {
    if (!occasion) return;
    setSeatingStyleId(occasion.activeSeatingStyleId || "classic");
  }, [occasion]);

  useEffect(() => {
    if (!occasionMenuOpen) return undefined;
    const onDoc = (e) => {
      if (!occasionMenuRef.current?.contains(e.target)) {
        setOccasionMenuOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOccasionMenuOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [occasionMenuOpen]);

  const selectBoardLayout = async (styleId, { closePicker = false } = {}) => {
    setSeatingStyleId(styleId);
    if (closePicker) setLayoutPickerOpen(false);
    if (!occasionId || isViewer) return;
    setSavingLayoutPick(true);
    try {
      const res = await axios.put(updateOccasion(occasionId), {
        activeSeatingStyleId: styleId,
      });
      const updated = res.data?.data || res.data;
      setOccasions((prev) =>
        prev.map((o) =>
          sameId(o._id, occasionId)
            ? {
                ...o,
                activeSeatingStyleId:
                  updated?.activeSeatingStyleId || styleId,
              }
            : o
        )
      );
    } catch (err) {
      console.error(err);
      toast.error("Could not save layout selection.");
    } finally {
      setSavingLayoutPick(false);
    }
  };

  useEffect(() => {
    const load = async () => {
      if (!selectedEvent?.eventId) return;
      try {
        const [occRes, guests, allocs] = await Promise.all([
          axios.get(getOccasionsByEventId(selectedEvent.eventId)),
          getMainGuestsAndAttendees(selectedEvent.eventId),
          axios.get(getSeatAllocation(selectedEvent.eventId)),
        ]);
        setOccasions(occRes.data?.data || []);
        setGuestWithAttendees(guests || []);
        setSavedAllocations(allocs.data?.data || []);
        const occList = occRes.data?.data || [];
        if (occList.length) {
          setOccasionId((prev) => prev || String(occList[0]._id));
        }
      } catch (err) {
        console.error(err);
        toast.error("Failed to load seat board data.");
      }

      try {
        const { formatted } = await fetchAllocations(selectedEvent.eventId);
        setSelectedAllocations(formatted || {});
      } catch (err) {
        console.error(err);
      }
    };
    load();
  }, [selectedEvent]);

  useEffect(() => {
    const loadRsvp = async () => {
      if (!selectedEvent?.eventId || !occasionId || !occasion?.RSVP) return;
      try {
        const res = await axios.get(getTrackedInvites(selectedEvent.eventId));
        const trackData = res.data?.data || [];
        const nextAlloc = {};
        const nextRsvp = {};
        trackData.forEach((invite) => {
          const key = `${invite.guestId}-${invite.attendeeId || "null"}`;
          nextAlloc[key] = invite.confirmedOccasionIds || [];
          const occResponses = {};
          (invite.rsvps || []).forEach((r) => {
            occResponses[r.occasionId] =
              r.response === "N/A" ? null : r.response;
          });
          nextRsvp[key] = occResponses;
        });
        setSelectedAllocations((prev) => ({ ...prev, ...nextAlloc }));
        setRsvpMap(nextRsvp);
      } catch (err) {
        console.error(err);
      }
    };
    loadRsvp();
  }, [selectedEvent, occasionId, occasion?.RSVP]);

  useEffect(() => {
    if (!occasionId) {
      setPlacements({});
      setViewMode("auto");
      setZoomByKind({ row: 1, table: 1 });
      return;
    }

    const next = {};
    const oid = String(occasionId);

    (savedAllocations || []).forEach((record) => {
      const guestId = idStr(record.guestId);
      const attendeeId = record.attendeeId ? idStr(record.attendeeId) : null;
      const personKey = `${guestId}-${attendeeId || "null"}`;
      const seat = (record.allocatedSeat || []).find((s) =>
        sameId(s.occasionId, oid)
      );
      if (!seat) return;
      const row = toNum(seat.rowAllocation?.rowNumber);
      const table = toNum(seat.tableAllocation?.tableNumber);
      const seatNo = toNum(
        seat.rowAllocation?.seatNumber ?? seat.tableAllocation?.seatNumber
      );
      if ((row || table) && seatNo) {
        next[personKey] = {
          row: row || null,
          table: table || null,
          seat: seatNo,
        };
      }
    });

    setPlacements(next);
    setViewMode("auto");
  }, [occasionId, savedAllocations]);

  const people = useMemo(() => {
    if (!occasionId) return [];
    const oid = String(occasionId);
    const rsvpRequired = Boolean(occasion?.RSVP);
    const list = [];

    (guestWithAttendees || []).forEach((entry) => {
      const guest = entry.guest || {};
      const guestId = idStr(guest._id);
      if (!guestId) return;
      const mainKey = `${guestId}-null`;
      const mainOnOccasion = (selectedAllocations[mainKey] || []).some((id) =>
        sameId(id, oid)
      );
      const mainRsvpOk =
        !rsvpRequired ||
        String(rsvpMap[mainKey]?.[oid] || "").toLowerCase() === "yes";

      if (mainOnOccasion && mainRsvpOk) {
        list.push({
          personKey: mainKey,
          guestId,
          attendeeId: null,
          partyId: guestId,
          role: "guest",
          name: personName(guest) || "Guest",
          firstName: guest.firstName || "",
          lastName: guest.lastName || "",
          relation: "",
          partyName: personName(guest) || "Guest",
        });
      }

      (entry.attendees || []).forEach((att) => {
        const attendeeId = idStr(att._id);
        if (!attendeeId) return;
        const key = `${guestId}-${attendeeId}`;
        const onOccasion = (selectedAllocations[key] || []).some((id) =>
          sameId(id, oid)
        );
        const rsvpOk =
          !rsvpRequired ||
          String(rsvpMap[key]?.[oid] || "").toLowerCase() === "yes";
        if (!onOccasion || !rsvpOk) return;
        list.push({
          personKey: key,
          guestId,
          attendeeId,
          partyId: guestId,
          role: "attendee",
          name: personName(att) || "Attendee",
          firstName: att.firstName || "",
          lastName: att.lastName || "",
          relation: att.relationWithMainMember || "Attendee",
          partyName: personName(guest) || "Guest",
        });
      });
    });

    return list;
  }, [
    occasionId,
    occasion?.RSVP,
    guestWithAttendees,
    selectedAllocations,
    rsvpMap,
  ]);

  const peopleByKey = useMemo(() => {
    const map = {};
    people.forEach((p) => {
      map[p.personKey] = p;
    });
    return map;
  }, [people]);

  const occupancy = useMemo(() => {
    const map = {};
    Object.entries(placements).forEach(([personKey, placement]) => {
      const key = seatKeyFromPlacement(placement);
      if (!key || !peopleByKey[personKey]) return;
      map[key] = { ...peopleByKey[personKey], ...placement };
    });
    return map;
  }, [placements, peopleByKey]);

  const layoutMeta = useMemo(
    () => buildOccasionSeatPlan(occasion),
    [occasion]
  );

  const seatsInRow = (rowNo) =>
    layoutMeta?.rowCounts?.[rowNo - 1] || 0;

  const seatsAtTable = (tableNo) =>
    layoutMeta?.tableCounts?.[tableNo - 1] || 0;

  const filteredPeople = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    const groups = {};
    people.forEach((person) => {
      // Seated people leave the list — they return when unseated from a seat.
      if (seatKeyFromPlacement(placements[person.personKey])) return;
      if (
        q &&
        !person.name.toLowerCase().includes(q) &&
        !person.partyName.toLowerCase().includes(q)
      ) {
        return;
      }
      const partyId = person.partyId;
      if (!groups[partyId]) {
        groups[partyId] = {
          partyId,
          partyName: person.partyName,
          guest: null,
          attendees: [],
        };
      }
      if (person.role === "guest") groups[partyId].guest = person;
      else groups[partyId].attendees.push(person);
    });
    return Object.values(groups)
      .filter((g) => g.guest || g.attendees.length)
      .sort((a, b) => a.partyName.localeCompare(b.partyName));
  }, [people, searchTerm, placements]);

  const unseatedCount = people.filter(
    (p) => !seatKeyFromPlacement(placements[p.personKey])
  ).length;
  const totalPeople = people.length;
  const seatedCount = Math.max(0, totalPeople - unseatedCount);

  const clearPersonPlacement = (personKey, draft) => {
    const next = { ...draft };
    delete next[personKey];
    return next;
  };

  const assignToSeat = (personKey, seatKey) => {
    if (isViewer) return;
    const parsed = parseSeatKey(seatKey);
    const person = peopleByKey[personKey];
    if (!parsed || !person) return;

    setPlacements((prev) => {
      let next = { ...prev };
      const existingKey = Object.keys(next).find((key) => {
        if (key === personKey) return false;
        return seatKeyFromPlacement(next[key]) === seatKey;
      });
      if (existingKey) {
        next = clearPersonPlacement(existingKey, next);
      }

      next[personKey] = {
        row: parsed.kind === "row" ? parsed.location : null,
        table: parsed.kind === "table" ? parsed.location : null,
        seat: parsed.seat,
      };
      return next;
    });
  };

  const unassignPerson = (personKey) => {
    if (isViewer) return;
    setPlacements((prev) => clearPersonPlacement(personKey, prev));
  };

  const cleanupDragGhost = () => {
    if (dragGhostRef.current) {
      dragGhostRef.current.remove();
      dragGhostRef.current = null;
    }
  };

  const onDragStartPerson = (event, person) => {
    if (isViewer) {
      event.preventDefault();
      return;
    }
    const payload = {
      personKey: person.personKey,
      name: person.name,
      role: person.role,
    };
    event.dataTransfer.setData(DND_TYPE, JSON.stringify(payload));
    event.dataTransfer.setData("text/plain", person.personKey);
    event.dataTransfer.effectAllowed = "move";

    cleanupDragGhost();
    const ghost = document.createElement("div");
    ghost.className = `sab-drag-ghost${
      person.role === "attendee" ? " is-attendee" : " is-guest"
    }`;
    ghost.textContent = seatInitial(person) || "·";
    ghost.setAttribute("aria-hidden", "true");
    document.body.appendChild(ghost);
    dragGhostRef.current = ghost;
    // Offset so only initials show as the drag preview (not the full seat/name).
    event.dataTransfer.setDragImage(ghost, 18, 18);

    setDraggingKey(person.personKey);
    setPinnedPartyId(null);
    setPinnedSeatKey(null);
    setHoveredPartyId(null);
    setHoveredSeatKey(null);
    setPartyCardPos(null);
  };

  const onDragEnd = () => {
    cleanupDragGhost();
    setDraggingKey(null);
    setDragOverSeat(null);
    setListDropActive(false);
  };

  useEffect(
    () => () => {
      cleanupDragGhost();
      if (partyLeaveTimerRef.current) {
        clearTimeout(partyLeaveTimerRef.current);
      }
    },
    []
  );

  const activePartyId = pinnedPartyId || hoveredPartyId;
  const activeFocusSeatKey = pinnedSeatKey || hoveredSeatKey;
  const partyActive = Boolean(activePartyId);

  const activePartyMembers = useMemo(() => {
    if (!activePartyId) return [];
    return people
      .filter((p) => String(p.partyId) === String(activePartyId))
      .map((p) => ({
        ...p,
        seatKey: seatKeyFromPlacement(placements[p.personKey]),
      }))
      .sort((a, b) => {
        if (a.role === "guest" && b.role !== "guest") return -1;
        if (b.role === "guest" && a.role !== "guest") return 1;
        return String(a.name || "").localeCompare(String(b.name || ""));
      });
  }, [activePartyId, people, placements]);

  const updatePartyCardPos = (el) => {
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const cardW = 280;
    const pad = 12;
    let x = rect.left + rect.width / 2 - cardW / 2;
    x = Math.max(pad, Math.min(x, window.innerWidth - cardW - pad));
    // Prefer above the seat when there is room; otherwise open below.
    const placeAbove = rect.top > 220;
    const y = placeAbove ? rect.top - 8 : rect.bottom + 8;
    setPartyCardPos({ x, y, width: cardW, placeAbove });
  };

  const openPartyFromSeat = (occupant, seatKey, el) => {
    if (!occupant?.partyId) return;
    if (partyLeaveTimerRef.current) {
      clearTimeout(partyLeaveTimerRef.current);
      partyLeaveTimerRef.current = null;
    }
    setPinnedPartyId(String(occupant.partyId));
    setPinnedSeatKey(seatKey);
    updatePartyCardPos(el);
  };

  const clearHoverParty = () => {
    if (pinnedPartyId) return;
    setHoveredPartyId(null);
    setHoveredSeatKey(null);
  };

  const closePinnedParty = () => {
    setPinnedPartyId(null);
    setPinnedSeatKey(null);
    setHoveredPartyId(null);
    setHoveredSeatKey(null);
    setPartyCardPos(null);
  };

  useEffect(() => {
    if (!pinnedPartyId) return undefined;
    const onPointerDown = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest(".sab-party-float") || target.closest(".sab-seat")) {
        return;
      }
      closePinnedParty();
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [pinnedPartyId]);

  const partyClassFor = (occupant, seatKey) => {
    if (
      !occupant?.partyId ||
      !activePartyId ||
      String(occupant.partyId) !== String(activePartyId)
    ) {
      return "";
    }
    return ` is-party-hover${
      activeFocusSeatKey === seatKey ? " is-party-focus" : " is-party-mate"
    }`;
  };

  const bindPartyHover = (occupant, seatKey) => ({
    onMouseEnter: () => {
      if (!occupant?.partyId || pinnedPartyId) return;
      setHoveredPartyId(String(occupant.partyId));
      setHoveredSeatKey(seatKey);
    },
    onMouseLeave: () => {
      clearHoverParty();
    },
    onClick: (e) => {
      if (!occupant) return;
      e.stopPropagation();
      if (
        pinnedPartyId &&
        pinnedSeatKey === seatKey &&
        String(pinnedPartyId) === String(occupant.partyId)
      ) {
        closePinnedParty();
        return;
      }
      openPartyFromSeat(occupant, seatKey, e.currentTarget);
    },
  });

  const renderPartyCard = () => {
    if (!pinnedPartyId || !partyCardPos || !activePartyMembers.length) {
      return null;
    }
    const sectionKind = parseSeatKey(pinnedSeatKey)?.kind;
    const sectionClass =
      sectionKind === "table"
        ? " is-section-table"
        : sectionKind === "row"
          ? " is-section-row"
          : "";
    return (
      <div
        className={`sab-party-float is-pinned${sectionClass}${
          partyCardPos.placeAbove ? " is-above" : " is-below"
        }`}
        style={{
          left: partyCardPos.x,
          top: partyCardPos.y,
          width: partyCardPos.width,
        }}
      >
        <div className="sab-party-float-head">
          <div>
            <span className="sab-party-float-kicker">Same party</span>
            <strong>
              {activePartyMembers.length}{" "}
              {activePartyMembers.length === 1 ? "person" : "people"}
            </strong>
          </div>
          <button
            type="button"
            className="sab-party-float-close"
            aria-label="Close party card"
            onClick={closePinnedParty}
          >
            ×
          </button>
        </div>
        <ul className="sab-party-float-list">
          {activePartyMembers.map((member) => {
            const isFocus = member.seatKey === activeFocusSeatKey;
            const rel = String(member.relation || "").trim();
            const showRelation =
              member.role === "attendee" &&
              rel &&
              !/^attendee$/i.test(rel);
            return (
              <li
                key={member.personKey}
                className={`sab-party-float-item is-${member.role}${
                  isFocus ? " is-focus" : ""
                }`}
              >
                <span className="sab-party-float-avatar">
                  {seatInitial(member)}
                </span>
                <span className="sab-party-float-copy">
                  <span className="sab-party-float-name">{member.name}</span>
                  {showRelation ? (
                    <span className="sab-party-float-meta">{rel}</span>
                  ) : null}
                  <span className="sab-party-float-seat">
                    {member.seatKey
                      ? seatLocationLabel(member.seatKey)
                      : "Not seated yet"}
                  </span>
                </span>
                {isFocus ? (
                  <em className="sab-party-float-viewing">Viewing</em>
                ) : null}
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  const readDragPayload = (event) => {
    const raw =
      event.dataTransfer.getData(DND_TYPE) ||
      event.dataTransfer.getData("text/plain");
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      return parsed?.personKey ? parsed : { personKey: raw };
    } catch {
      return { personKey: raw };
    }
  };

  const onDropSeat = (event, seatKey) => {
    event.preventDefault();
    const payload = readDragPayload(event);
    setDragOverSeat(null);
    if (!payload?.personKey) return;
    assignToSeat(payload.personKey, seatKey);
  };

  const onDropList = (event) => {
    event.preventDefault();
    const payload = readDragPayload(event);
    setListDropActive(false);
    if (!payload?.personKey) return;
    unassignPerson(payload.personKey);
  };

  const handleSave = async () => {
    if (!occasion || !occasionId) {
      toast.error("Select an occasion first.");
      return;
    }
    if (isViewer) return;

    const oid = String(occasionId);
    const type = allocationType || "row";
    const payloadList = [];

    Object.entries(placements).forEach(([personKey, placement]) => {
      const person = peopleByKey[personKey];
      if (!person || !placement?.seat) return;
      if (!placement.row && !placement.table) return;

      let rowAllocation = null;
      let tableAllocation = null;
      if (type === "row" || (type === "both" && placement.row)) {
        rowAllocation = {
          rowNumber: placement.row || null,
          seatNumber: placement.seat,
        };
      }
      if (type === "table" || (type === "both" && placement.table)) {
        tableAllocation = {
          tableNumber: placement.table || null,
          seatNumber: placement.seat,
        };
      }
      if (type === "both" && placement.row && !placement.table) {
        tableAllocation = null;
      }
      if (type === "both" && placement.table && !placement.row) {
        rowAllocation = null;
      }

      payloadList.push({
        eventId: selectedEvent?.eventId,
        guestId: person.guestId,
        attendeeId: person.attendeeId,
        allocatedSeat: [
          {
            occasionId: oid,
            allocationType: type,
            rowAllocation,
            tableAllocation,
            remarks: "",
          },
        ],
        enteredBy: eventEntity?.username || "admin",
        modifiedBy: eventEntity?.username || "admin",
      });
    });

    if (!payloadList.length) {
      toast.error("Place at least one guest on a seat before saving.");
      return;
    }

    setSaving(true);
    try {
      await Promise.all(
        payloadList.map((item) => axios.post(allocateSeat(), item))
      );
      const refreshed = await axios.get(getSeatAllocation(selectedEvent.eventId));
      setSavedAllocations(refreshed.data?.data || []);
      toast.success("Seat layout saved.");
    } catch (err) {
      console.error(err);
      toast.error("Failed to save seat layout.");
    } finally {
      setSaving(false);
    }
  };

  const adjustZoom = (kind, delta) => {
    setZoomByKind((prev) => {
      const next = Math.round((prev[kind] + delta) * 100) / 100;
      return {
        ...prev,
        [kind]: Math.min(2.4, Math.max(0.45, next)),
      };
    });
  };

  const resetZoom = (kind) => {
    setZoomByKind((prev) => ({ ...prev, [kind]: 1 }));
  };

  const onCanvasWheel = (event, kind) => {
    // Scroll wheel zooms the map; keep page from scrolling.
    event.preventDefault();
    event.stopPropagation();
    adjustZoom(kind, event.deltaY > 0 ? -0.08 : 0.08);
  };

  const renderPersonCard = (person) => (
    <div
      key={person.personKey}
      className={`sab-person${
        person.role === "guest" ? " is-guest" : " is-attendee"
      }${draggingKey === person.personKey ? " is-dragging" : ""}`}
      draggable={!isViewer}
      onDragStart={(e) => onDragStartPerson(e, person)}
      onDragEnd={onDragEnd}
    >
      <span className="sab-person-avatar">{seatInitial(person)}</span>
      <span className="sab-person-copy">
        <span className="sab-person-name">{person.name}</span>
        <span className="sab-person-meta">
          {person.role === "guest"
            ? "Main guest"
            : person.relation || "Attendee"}
        </span>
      </span>
    </div>
  );

  const renderZoomControls = (kind) => (
    <div className="sab-zoom-controls">
      <button
        type="button"
        className="sab-zoom-btn"
        title="Zoom out"
        onClick={() => adjustZoom(kind, -0.1)}
      >
        <i className="bi bi-zoom-out" aria-hidden="true" />
      </button>
      <button
        type="button"
        className="sab-zoom-label"
        title="Reset zoom"
        onClick={() => resetZoom(kind)}
      >
        {Math.round(zoomByKind[kind] * 100)}%
      </button>
      <button
        type="button"
        className="sab-zoom-btn"
        title="Zoom in"
        onClick={() => adjustZoom(kind, 0.1)}
      >
        <i className="bi bi-zoom-in" aria-hidden="true" />
      </button>
    </div>
  );

  const renderMapPanel = (kind) => {
    const isRow = kind === "row";
    const zoom = zoomByKind[kind] || 1;
    const dropParsed = dragOverSeat ? parseSeatKey(dragOverSeat) : null;
    const showDropHint =
      Boolean(draggingKey) &&
      dropParsed &&
      ((isRow && dropParsed.kind === "row") ||
        (!isRow && dropParsed.kind === "table"));

    return (
      <section
        className={`sab-map-panel sab-map-${kind}${
          showDropHint ? " is-drop-hinting" : ""
        }`}
      >
        <div className={`sab-map-head sab-map-head-${kind}`}>
          <div className="sab-map-head-title">
            <span className="sab-map-kicker">
              {isRow ? "Row seating" : "Table seating"}
            </span>
            <strong>{isRow ? "Rows" : "Tables"}</strong>
          </div>
          <div className="sab-map-head-actions">
            {layoutMeta ? (
              <span className="sab-map-stat-pill">
                {isRow
                  ? `${layoutMeta.rowCounts.reduce((s, n) => s + n, 0)} seats`
                  : `${layoutMeta.tableCounts.reduce((s, n) => s + n, 0)} seats`}
                {layoutMeta.capacity != null
                  ? ` · capacity ${layoutMeta.capacity}`
                  : ""}
              </span>
            ) : null}
            {renderZoomControls(kind)}
          </div>
        </div>
        <div
          className={`sab-map-canvas sa-layout-canvas${
            partyActive ? " is-party-active" : ""
          }`}
          onWheel={(e) => onCanvasWheel(e, kind)}
        >
          {showDropHint ? (
            <div className="sab-drop-hint-layer" aria-live="polite">
              <div className="sab-drop-hint">
                <div className="sab-drop-hint-text">
                  <span className="sab-drop-hint-kicker">Drop on</span>
                  <span className="sab-drop-hint-meta">
                    {isRow
                      ? `Row ${dropParsed.location}`
                      : `Table ${dropParsed.location}`}
                  </span>
                </div>
                <strong className="sab-drop-hint-seat">{dropParsed.seat}</strong>
              </div>
            </div>
          ) : null}
          <div className="sab-zoom-viewport">
            <div
              className="sab-zoom-content"
              style={{
                transform: `scale(${zoom})`,
              }}
            >
              {isRow ? renderRowLayout() : renderTableLayout()}
            </div>
          </div>
        </div>
      </section>
    );
  };

  const renderSeatButton = (seatKey, label) => {
    const occupant = occupancy[seatKey];
    const roleClass = occupant
      ? occupant.role === "guest"
        ? "is-guest"
        : "is-attendee"
      : "is-empty";
    return (
      <button
        key={seatKey}
        type="button"
        className={`sab-seat sa-layout-seat ${roleClass}${
          dragOverSeat === seatKey ? " is-drop-target" : ""
        }${
          draggingKey && occupant?.personKey === draggingKey
            ? " is-dragging"
            : ""
        }${partyClassFor(occupant, seatKey)}`}
        draggable={Boolean(occupant) && !isViewer}
        onDragStart={(e) => {
          if (!occupant) return;
          onDragStartPerson(e, occupant);
        }}
        onDragEnd={onDragEnd}
        onDragOver={(e) => {
          if (isViewer) return;
          e.preventDefault();
          e.dataTransfer.dropEffect = "move";
          setDragOverSeat(seatKey);
        }}
        onDragLeave={() => {
          setDragOverSeat((prev) => (prev === seatKey ? null : prev));
        }}
        onDrop={(e) => onDropSeat(e, seatKey)}
        {...bindPartyHover(occupant, seatKey)}
        title={
          occupant
            ? `${occupant.name} · ${
                occupant.role === "guest"
                  ? "Main guest"
                  : occupant.relation || "Attendee"
              }`
            : `Seat ${label} · Drop here`
        }
      >
        <span>{occupant ? seatInitial(occupant) || label : label}</span>
        {occupant && !isViewer ? (
          <span
            className="sab-seat-clear"
            role="presentation"
            onClick={(e) => {
              e.stopPropagation();
              closePinnedParty();
              unassignPerson(occupant.personKey);
            }}
          >
            ×
          </span>
        ) : null}
      </button>
    );
  };

  const renderRowSeatsStrip = (rowNo, seatNumbers, extraClass = "") => (
    <div className={`sa-row-seats sab-style-seats${extraClass}`}>
      {seatNumbers.map((seatNo) =>
        renderSeatButton(`row-${rowNo}-${seatNo}`, seatNo)
      )}
    </div>
  );

  const splitSeats = (count) => {
    const left = Math.floor(count / 2);
    const right = count - left;
    const leftSeats = Array.from({ length: left }, (_, i) => i + 1);
    const rightSeats = Array.from(
      { length: right },
      (_, i) => left + i + 1
    );
    return { leftSeats, rightSeats };
  };

  /** Split global rows into N sections (closest-first within each). */
  const splitRowsIntoSections = (totalRows, sectionCount) => {
    const n = Math.max(1, totalRows);
    const count = Math.max(1, sectionCount);
    const base = Math.floor(n / count);
    const rem = n % count;
    const sections = [];
    let cursor = 1;
    for (let s = 0; s < count; s += 1) {
      const size = base + (s < rem ? 1 : 0);
      const rows = [];
      for (let i = 0; i < size; i += 1) {
        rows.push(cursor);
        cursor += 1;
      }
      sections.push(rows);
    }
    return sections;
  };

  const renderCustomFloor = () => {
    if (!layoutMeta || !seatingStyle?.isCustom) return null;
    const stage = seatingStyle.stage;
    const blocks = Array.isArray(seatingStyle.blocks)
      ? seatingStyle.blocks
      : [];
    const seatsPerRow = layoutMeta.seatsPerRow;
    const seatsPerTable = layoutMeta.seatsPerTable;
    let remaining =
      layoutMeta.capacity == null
        ? Number.POSITIVE_INFINITY
        : layoutMeta.capacity;

    return (
      <div className="sab-custom-floor is-live">
        {stage ? (
          <div
            className={`sab-custom-stage is-live is-${stage.type || "stage"}`}
            style={{
              left: `${stage.x}%`,
              top: `${stage.y}%`,
              width: `${stage.w}%`,
              height: `${stage.h}%`,
            }}
          >
            {stage.label || "Stage"}
          </div>
        ) : null}
        {blocks.map((block) => {
          if (remaining <= 0) return null;
          if (block.kind === "row") {
            const wanted = Math.max(
              1,
              Number(block.seatCount) || seatsPerRow
            );
            const seatCount =
              remaining === Number.POSITIVE_INFINITY
                ? wanted
                : Math.min(wanted, remaining);
            remaining -= seatCount;
            if (seatCount <= 0) return null;
            const seats = Array.from({ length: seatCount }, (_, i) => i + 1);
            return (
              <div
                key={block.id}
                className="sab-custom-block is-row is-live"
                style={{
                  left: `${block.x}%`,
                  top: `${block.y}%`,
                  width: "auto",
                  height: "auto",
                }}
              >
                <div className="sa-row-label sab-custom-block-label">
                  Row {block.location}
                </div>
                <div className="sab-custom-live-seats sa-row-seats sab-style-seats">
                  {seats.map((seatNo) =>
                    renderSeatButton(
                      `row-${block.location}-${seatNo}`,
                      seatNo
                    )
                  )}
                </div>
              </div>
            );
          }

          const wanted = Math.max(
            1,
            Number(block.seatCount) || seatsPerTable
          );
          const seatCount =
            remaining === Number.POSITIVE_INFINITY
              ? wanted
              : Math.min(wanted, remaining);
          remaining -= seatCount;
          if (seatCount <= 0) return null;
          const seatBtn = seatCount > 16 ? 16 : seatCount > 10 ? 18 : 22;
          const surface = Math.max(70, Math.min(112, 58 + seatCount * 3));
          const rad = surface / 2 + seatBtn / 2 + 3;
          const outer = surface + (seatBtn + 12) * 2;

          return (
            <div
              key={block.id}
              className="sab-custom-block is-table is-live"
              style={{
                left: `${block.x}%`,
                top: `${block.y}%`,
                width: outer,
                height: outer,
              }}
            >
              <div
                className="sab-table-wrap is-round"
                style={{
                  width: outer,
                  height: outer,
                  position: "relative",
                }}
              >
                <div
                  className="sab-table-surface is-round"
                  style={{
                    width: surface,
                    height: surface,
                    left: "50%",
                    top: "50%",
                    transform: "translate(-50%, -50%)",
                    position: "absolute",
                  }}
                >
                  <span className="sab-table-label">T{block.location}</span>
                </div>
                {Array.from({ length: seatCount }, (_, seatIndex) => {
                  const seatNo = seatIndex + 1;
                  const angleDeg = (360 / seatCount) * seatIndex - 90;
                  const angleRad = (angleDeg * Math.PI) / 180;
                  return renderTableSeatButton(
                    block.location,
                    seatNo,
                    seatBtn,
                    {
                      left: `calc(50% + ${Math.cos(angleRad) * rad}px)`,
                      top: `calc(50% + ${Math.sin(angleRad) * rad}px)`,
                      transform: "translate(-50%, -50%)",
                    }
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderCustomMapPanel = () => {
    const zoom = zoomByKind.row || 1;
    const styleId = seatingStyle?.id;
    const isCustom = Boolean(seatingStyle?.isCustom);
    const applies = seatingStyle?.appliesTo || "both";
    // Advanced mode: one floor — rows and tables can share the same layout.
    const showRows =
      isCustom || applies === "row" || applies === "both";
    const showTables =
      isCustom || applies === "table" || applies === "both";

    let floor = null;
    if (isCustom) {
      floor = renderCustomFloor();
    } else {
      floor = (
        <div className="sab-advanced-unified">
          {showRows ? renderRowLayout() : null}
          {showRows && showTables ? (
            <div className="sab-style-divider">Tables / reception</div>
          ) : null}
          {showTables ? renderTableLayout() : null}
        </div>
      );
    }

    return (
      <section className="sab-map-panel sab-map-custom">
        <div className="sab-map-head sab-map-head-row">
          <div className="sab-map-head-title">
            <span className="sab-map-kicker">
              Layout · {seatingStyle.name}
              {seatingStyle.status === "draft" ? " (Draft)" : ""}
            </span>
            <strong>
              {isCustom
                ? "Custom floor plan"
                : applies === "both"
                  ? "Hybrid seating floor"
                  : applies === "table"
                    ? "Table floor"
                    : "Row floor"}
            </strong>
          </div>
          <div className="sab-map-head-actions">
            {layoutMeta ? (
              <span className="sab-map-stat-pill">
                {layoutMeta.totalSeats} seats
                {layoutMeta.capacity != null
                  ? ` · capacity ${layoutMeta.capacity}`
                  : ""}
              </span>
            ) : null}
            {renderZoomControls("row")}
          </div>
        </div>
        <div
          className={`sab-map-canvas sa-layout-canvas${
            partyActive ? " is-party-active" : ""
          }`}
          onWheel={(e) => onCanvasWheel(e, "row")}
        >
          <div className="sab-zoom-viewport">
            <div
              className="sab-zoom-content"
              style={{ transform: `scale(${zoom})` }}
            >
              {floor}
            </div>
          </div>
        </div>
      </section>
    );
  };

  const renderRowLayout = () => {
    if (!layoutMeta) return null;
    if (seatingStyle?.isCustom) return renderCustomFloor();
    const styleId = seatingStyle.id;
    const { rows } = layoutMeta;

    if (styleId === "traditional" || styleId === "ceremony") {
      return (
        <div className={`sab-style-layout is-${styleId}`}>
          <div className="sab-style-stage is-front">
            {styleId === "ceremony" ? "Altar / Mandap" : "Stage"}
          </div>
          <p className="sab-facing-legend mb-0">
            Row 1 faces the stage · Left / Right are aisle sides
          </p>
          <div className="sab-style-aisle-rows">
            {Array.from({ length: rows }, (_, rowIndex) => {
              const rowNo = rowIndex + 1;
              const { leftSeats, rightSeats } = splitSeats(seatsInRow(rowNo));
              return (
                <div className="sab-style-aisle-row" key={`row-${rowNo}`}>
                  <span className="sab-style-row-tag" title="Left of aisle">
                    L{rowNo}
                  </span>
                  {renderRowSeatsStrip(rowNo, leftSeats, " is-left")}
                  <div className="sab-style-aisle" aria-hidden="true">
                    {rowIndex === 0 ? "Aisle" : ""}
                  </div>
                  {renderRowSeatsStrip(rowNo, rightSeats, " is-right")}
                  <span className="sab-style-row-tag" title="Right of aisle">
                    R{rowNo}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    if (styleId === "proscenium") {
      return (
        <div className="sab-style-layout is-proscenium">
          <div className="sab-style-stage is-proscenium-curve" aria-hidden="true">
            <span>Stage</span>
          </div>
          <p className="sab-facing-legend mb-0">
            Curved proscenium · <strong>Row 1</strong> is closest to the stage ·
            seats fan outward as Row → Seat (R1-S1…)
          </p>
          <div className="sab-style-fan is-proscenium-fan">
            {Array.from({ length: rows }, (_, rowIndex) => {
              const rowNo = rowIndex + 1;
              const seats = Array.from(
                { length: seatsInRow(rowNo) },
                (_, i) => i + 1
              );
              const fanPad = Math.round(
                ((rows - 1 - rowIndex) / Math.max(rows - 1, 1)) * 36
              );
              const curve =
                rowIndex === 0
                  ? 0
                  : Math.round((rowIndex / Math.max(rows - 1, 1)) * 10);
              return (
                <div
                  className="sab-style-fan-row"
                  key={`row-${rowNo}`}
                  style={{
                    paddingLeft: fanPad,
                    paddingRight: fanPad,
                    transform: curve ? `translateY(${curve * 0.35}px)` : undefined,
                  }}
                >
                  <span className="sab-style-row-tag" title={`Row ${rowNo}`}>
                    R{rowNo}
                  </span>
                  {renderRowSeatsStrip(rowNo, seats)}
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    if (styleId === "traverse") {
      return (
        <div className="sab-style-layout is-traverse">
          <p className="sab-facing-legend sab-facing-legend-full mb-0">
            Both banks face the runway. <strong>Left · Row 1</strong> and{" "}
            <strong>Right · Row 1</strong> are the first seats parallel to the
            stage on each side (same facing order, different banks).
          </p>
          <div className="sab-style-traverse-body">
            <div className="sab-style-traverse-side is-left">
              <span className="sab-style-bank-title">Left bank</span>
              {Array.from({ length: rows }, (_, rowIndex) => {
                const rowNo = rowIndex + 1;
                const { leftSeats } = splitSeats(seatsInRow(rowNo));
                return (
                  <div className="sab-style-traverse-row" key={`row-l-${rowNo}`}>
                    <span
                      className="sab-style-row-tag is-bank"
                      title="Left bank · faces runway"
                    >
                      Left · Row {rowNo}
                    </span>
                    {renderRowSeatsStrip(rowNo, leftSeats)}
                  </div>
                );
              })}
            </div>
            <div className="sab-style-stage is-runway">Runway / Stage</div>
            <div className="sab-style-traverse-side is-right">
              <span className="sab-style-bank-title">Right bank</span>
              {Array.from({ length: rows }, (_, rowIndex) => {
                const rowNo = rowIndex + 1;
                const { rightSeats } = splitSeats(seatsInRow(rowNo));
                return (
                  <div className="sab-style-traverse-row" key={`row-r-${rowNo}`}>
                    {renderRowSeatsStrip(rowNo, rightSeats)}
                    <span
                      className="sab-style-row-tag is-bank"
                      title="Right bank · faces runway"
                    >
                      Right · Row {rowNo}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      );
    }

    if (styleId === "arena" || styleId === "u-shape") {
      const isU = styleId === "u-shape";
      const sectionDefs = isU
        ? [
            { code: "B", label: "Left", side: "left" },
            { code: "C", label: "Right", side: "right" },
            { code: "A", label: "Base", side: "bottom" },
          ]
        : [
            { code: "A", label: "Front", side: "top" },
            { code: "B", label: "Right", side: "right" },
            { code: "C", label: "Back", side: "bottom" },
            { code: "D", label: "Left", side: "left" },
          ];
      const sectionRows = splitRowsIntoSections(rows, sectionDefs.length);

      const block = (def, rowList) => {
        if (!rowList?.length) return null;
        return (
          <div
            className={`sab-style-arena-block is-${def.side}`}
            key={`sec-${def.code}`}
          >
            <span className="sab-style-bank-title">
              Section {def.code} · {def.label}
            </span>
            {rowList.map((rowNo, idx) => {
              const localRow = idx + 1;
              return (
                <div
                  className="sab-style-arena-row"
                  key={`${def.code}-${rowNo}`}
                >
                  <span
                    className="sab-style-row-tag is-bank"
                    title={`Section ${def.code} · Row ${localRow} (closest = R1)`}
                  >
                    {def.code}-R{localRow}
                  </span>
                  {renderRowSeatsStrip(
                    rowNo,
                    Array.from({ length: seatsInRow(rowNo) }, (_, i) => i + 1),
                    def.side === "left" || def.side === "right"
                      ? " is-vertical"
                      : ""
                  )}
                </div>
              );
            })}
          </div>
        );
      };

      const bySide = Object.fromEntries(
        sectionDefs.map((def, i) => [def.side, block(def, sectionRows[i])])
      );

      return (
        <div className={`sab-style-layout is-${styleId}`}>
          <p className="sab-facing-legend mb-0">
            {isU ? "U-shape" : "Arena / in-the-round"} · Section → Row → Seat.
            In each section, <strong>R1</strong> is nearest the center stage.
          </p>
          {isU ? (
            <div className="sab-style-stage is-front is-soft">Stage</div>
          ) : (
            bySide.top
          )}
          <div className="sab-style-arena-mid">
            {bySide.left}
            <div className="sab-style-stage is-center">
              {isU ? "Open floor" : "Stage / Mandap"}
            </div>
            {bySide.right}
          </div>
          {bySide.bottom}
        </div>
      );
    }

    if (styleId === "courtyard") {
      const ceremonyRows = Math.max(1, Math.ceil(rows * 0.55));
      return (
        <div className="sab-style-layout is-courtyard">
          <div className="sab-style-stage is-front">Altar / Mandap</div>
          <p className="sab-facing-legend mb-0">
            Row 1 faces the altar · L / R = aisle sides
          </p>
          <div className="sab-style-aisle-rows">
            {Array.from({ length: ceremonyRows }, (_, rowIndex) => {
              const rowNo = rowIndex + 1;
              const { leftSeats, rightSeats } = splitSeats(seatsInRow(rowNo));
              return (
                <div className="sab-style-aisle-row" key={`row-${rowNo}`}>
                  <span className="sab-style-row-tag">L{rowNo}</span>
                  {renderRowSeatsStrip(rowNo, leftSeats, " is-left")}
                  <div className="sab-style-aisle" aria-hidden="true" />
                  {renderRowSeatsStrip(rowNo, rightSeats, " is-right")}
                  <span className="sab-style-row-tag">R{rowNo}</span>
                </div>
              );
            })}
          </div>
          {ceremonyRows < rows ? (
            <>
              <div className="sab-style-divider">Overflow row seating</div>
              <div className="sa-row-layout sab-row-layout">
                {Array.from(
                  { length: rows - ceremonyRows },
                  (_, rowIndex) => {
                    const rowNo = ceremonyRows + rowIndex + 1;
                    const seats = Array.from(
                      { length: seatsInRow(rowNo) },
                      (_, i) => i + 1
                    );
                    return (
                      <div className="sa-row-block" key={`row-${rowNo}`}>
                        <div className="sa-row-label">Row {rowNo}</div>
                        {renderRowSeatsStrip(rowNo, seats)}
                      </div>
                    );
                  }
                )}
              </div>
            </>
          ) : null}
        </div>
      );
    }

    // classic rows
    return (
      <div className="sa-row-layout sab-row-layout sab-style-layout is-classic">
        <p className="sab-facing-legend mb-0">
          Row 1 is closest to the front / stage
        </p>
        {Array.from({ length: rows }, (_, rowIndex) => {
          const rowNo = rowIndex + 1;
          const seats = Array.from({ length: seatsInRow(rowNo) }, (_, i) => i + 1);
          return (
            <div className="sa-row-block" key={`row-${rowNo}`}>
              <div className="sa-row-label">Row {rowNo}</div>
              {renderRowSeatsStrip(rowNo, seats)}
            </div>
          );
        })}
      </div>
    );
  };

  const renderTableSeatButton = (
    tableNo,
    seatNo,
    seatBtn,
    positionStyle
  ) => {
    const key = `table-${tableNo}-${seatNo}`;
    const occupant = occupancy[key];
    const roleClass = occupant
      ? occupant.role === "guest"
        ? "is-guest"
        : "is-attendee"
      : "is-empty";
    return (
      <button
        key={key}
        type="button"
        className={`sab-seat sa-table-seat ${roleClass}${
          dragOverSeat === key ? " is-drop-target" : ""
        }${partyClassFor(occupant, key)}`}
        style={{
          ...positionStyle,
          width: seatBtn,
          height: seatBtn,
          fontSize: seatBtn <= 18 ? 9 : seatBtn <= 22 ? 10 : 11,
        }}
        draggable={Boolean(occupant) && !isViewer}
        onDragStart={(e) => {
          if (!occupant) return;
          onDragStartPerson(e, occupant);
        }}
        onDragEnd={onDragEnd}
        onDragOver={(e) => {
          if (isViewer) return;
          e.preventDefault();
          e.dataTransfer.dropEffect = "move";
          setDragOverSeat(key);
        }}
        onDragLeave={() => {
          setDragOverSeat((prev) => (prev === key ? null : prev));
        }}
        onDrop={(e) => onDropSeat(e, key)}
        {...bindPartyHover(occupant, key)}
        title={
          occupant
            ? `${occupant.name} · ${
                occupant.role === "guest"
                  ? "Main guest"
                  : occupant.relation || "Attendee"
              }`
            : `Table ${tableNo} · Seat ${seatNo}`
        }
      >
        {occupant ? seatInitial(occupant) || seatNo : seatNo}
        {occupant && !isViewer ? (
          <span
            className="sab-seat-clear"
            role="presentation"
            onClick={(e) => {
              e.stopPropagation();
              closePinnedParty();
              unassignPerson(occupant.personKey);
            }}
          >
            ×
          </span>
        ) : null}
      </button>
    );
  };

  const renderTableLayout = () => {
    if (!layoutMeta) return null;
    const seats = layoutMeta.seatsPerTable;
    const styleId = seatingStyle.id;
    const wideTables = seats > 20;
    const seatBtn = wideTables
      ? seats <= 28
        ? 20
        : 18
      : seats <= 8
        ? 28
        : seats <= 12
          ? 24
          : seats <= 16
            ? 22
            : 20;

    const isLong = styleId === "long-table";
    const isCabaret = styleId === "cabaret";
    const isHeadTable = styleId === "head-table";
    const isUTables = styleId === "u-shape";

    const roundSize = wideTables
      ? Math.max(176, Math.min(220, 110 + seats * 3.2))
      : Math.max(128, Math.min(160, 96 + seats * 4.5));

    const tables = Array.from({ length: layoutMeta.tables }, (_, i) => i + 1);

    const placeAlongEdge = (count, index, halfSpan) => {
      if (count <= 1) return 0;
      return ((index / (count - 1)) * 2 - 1) * halfSpan;
    };

    const tableCard = (tableNo, variant = null) => {
      const seats = seatsAtTable(tableNo);
      const asHead = variant === "head" || (isHeadTable && tableNo === 1);
      const asLong = variant === "long" || isLong;
      const asCabaret = variant === "cabaret" || (isCabaret && !asHead);
      const asRound = !asHead && !asLong && !asCabaret;

      // —— Family long rectangle ——
      if (asLong) {
        const surfaceW = Math.max(168, Math.min(260, 96 + seats * 12));
        const surfaceH = Math.max(52, Math.min(64, 40 + seatBtn * 0.35));
        const gap = seatBtn / 2 + 5;
        const useEnds = seats >= 6;
        const endCount = useEnds ? 2 : 0;
        const along = seats - endCount;
        const topCount = Math.ceil(along / 2);
        const bottomCount = along - topCount;
        const halfSpan = surfaceW / 2 - seatBtn * 0.55;
        const outerW = surfaceW + (seatBtn + 12) * 2;
        const outerH = surfaceH + (seatBtn + 12) * 2;

        const longPos = (seatIndex) => {
          let x = 0;
          let y = 0;
          if (seatIndex < topCount) {
            x = placeAlongEdge(topCount, seatIndex, halfSpan);
            y = -(surfaceH / 2 + gap);
          } else if (seatIndex < topCount + bottomCount) {
            const idx = seatIndex - topCount;
            x = placeAlongEdge(bottomCount, idx, halfSpan);
            y = surfaceH / 2 + gap;
          } else if (seatIndex === topCount + bottomCount) {
            x = -(surfaceW / 2 + gap);
            y = 0;
          } else {
            x = surfaceW / 2 + gap;
            y = 0;
          }
          return {
            left: `calc(50% + ${x}px)`,
            top: `calc(50% + ${y}px)`,
            transform: "translate(-50%, -50%)",
          };
        };

        return (
          <div className="sa-table-card sab-table-card is-long-card" key={`table-${tableNo}`}>
            <div
              className="sab-table-wrap is-rect"
              style={{ width: outerW, height: outerH }}
            >
              <div
                className="sab-table-surface is-rect"
                style={{ width: surfaceW, height: surfaceH }}
              >
                <span className="sab-table-label">T{tableNo}</span>
              </div>
              {Array.from({ length: seats }, (_, seatIndex) =>
                renderTableSeatButton(
                  tableNo,
                  seatIndex + 1,
                  seatBtn,
                  longPos(seatIndex)
                )
              )}
            </div>
            <div className="sa-table-caption">Table {tableNo}</div>
          </div>
        );
      }

      // —— Wedding head table (one-sided toward guests) ——
      if (asHead) {
        const surfaceW = Math.max(300, Math.min(560, 160 + seats * 26));
        const surfaceH = 64;
        const gap = seatBtn / 2 + 8;
        const halfSpan = surfaceW / 2 - seatBtn * 0.6;
        const outerW = surfaceW + seatBtn + 24;
        const outerH = surfaceH + seatBtn * 2 + 36;

        const headPos = (seatIndex) => {
          const x = placeAlongEdge(seats, seatIndex, halfSpan);
          // Sit on the guest-facing edge (bottom), looking toward guests
          const y = surfaceH / 2 + gap;
          return {
            left: `calc(50% + ${x}px)`,
            top: `calc(50% + ${y}px)`,
            transform: "translate(-50%, -50%)",
          };
        };

        return (
          <div
            className="sa-table-card sab-table-card is-head-table"
            key={`table-${tableNo}`}
          >
            <div
              className="sab-table-wrap is-rect is-head"
              style={{ width: outerW, height: outerH }}
            >
              <div
                className="sab-table-surface is-rect is-head"
                style={{ width: surfaceW, height: surfaceH }}
              >
                <span className="sab-table-label">Head Table</span>
              </div>
              {Array.from({ length: seats }, (_, seatIndex) =>
                renderTableSeatButton(
                  tableNo,
                  seatIndex + 1,
                  seatBtn,
                  headPos(seatIndex)
                )
              )}
            </div>
            <div className="sa-table-caption">Head Table · faces guests</div>
          </div>
        );
      }

      // —— Cabaret: round table, ~¾ seats, open toward stage ——
      if (asCabaret) {
        const surface = roundSize;
        const rad = surface / 2 + seatBtn / 2 + 5;
        const outer = surface + (seatBtn + 16) * 2;
        const span = 270;
        const openGap = 90;
        const startDeg = -90 + openGap / 2;

        const cabPos = (seatIndex) => {
          const angleDeg =
            seats === 1
              ? 90
              : startDeg + (span / Math.max(seats - 1, 1)) * seatIndex;
          const angleRad = (angleDeg * Math.PI) / 180;
          const x = Math.cos(angleRad) * rad;
          const y = Math.sin(angleRad) * rad;
          return {
            left: `calc(50% + ${x}px)`,
            top: `calc(50% + ${y}px)`,
            transform: "translate(-50%, -50%)",
          };
        };

        return (
          <div
            className="sa-table-card sab-table-card is-cabaret-card"
            key={`table-${tableNo}`}
          >
            <div
              className="sab-table-wrap is-cabaret"
              style={{ width: outer, height: outer }}
            >
              <div className="sab-cabaret-open" aria-hidden="true">
                Open → stage
              </div>
              <div
                className="sab-table-surface is-round is-cabaret"
                style={{ width: surface, height: surface }}
              >
                <span className="sab-table-label">T{tableNo}</span>
              </div>
              {Array.from({ length: seats }, (_, seatIndex) =>
                renderTableSeatButton(
                  tableNo,
                  seatIndex + 1,
                  seatBtn,
                  cabPos(seatIndex)
                )
              )}
            </div>
            <div className="sa-table-caption">Table {tableNo}</div>
          </div>
        );
      }

      // —— Banquet / default rounds ——
      const surface = roundSize;
      const rad = surface / 2 + seatBtn / 2 + 4;
      const outer = surface + (seatBtn + 14) * 2;
      const roundPos = (seatIndex) => {
        const angleDeg = (360 / seats) * seatIndex - 90;
        const angleRad = (angleDeg * Math.PI) / 180;
        return {
          left: `calc(50% + ${Math.cos(angleRad) * rad}px)`,
          top: `calc(50% + ${Math.sin(angleRad) * rad}px)`,
          transform: "translate(-50%, -50%)",
        };
      };

      return (
        <div
          className={`sa-table-card sab-table-card${
            asRound ? " is-round-card" : ""
          }`}
          key={`table-${tableNo}`}
        >
          <div
            className="sab-table-wrap is-round"
            style={{ width: outer, height: outer }}
          >
            <div
              className="sab-table-surface is-round"
              style={{ width: surface, height: surface }}
            >
              <span className="sab-table-label">T{tableNo}</span>
            </div>
            {Array.from({ length: seats }, (_, seatIndex) =>
              renderTableSeatButton(
                tableNo,
                seatIndex + 1,
                seatBtn,
                roundPos(seatIndex)
              )
            )}
          </div>
          <div className="sa-table-caption">Table {tableNo}</div>
        </div>
      );
    };

    const cellMin = isLong
      ? Math.max(220, Math.min(320, 160 + seats * 12))
      : isCabaret
        ? roundSize + (seatBtn + 16) * 2 + 24
        : roundSize + (seatBtn + 14) * 2 + 24;

    if (isUTables) {
      const topCount = Math.max(1, Math.ceil(tables.length / 3));
      const sideCount = Math.max(
        0,
        Math.floor((tables.length - topCount) / 2)
      );
      const top = tables.slice(0, topCount);
      const left = tables.slice(topCount, topCount + sideCount);
      const right = tables.slice(topCount + sideCount);

      return (
        <div className="sab-style-layout is-u-tables">
          <div className="sab-style-stage is-front is-soft">Stage / Speakers</div>
          <p className="sab-facing-legend mb-0">
            U-shape dining · Table → Seat
          </p>
          <div className="sab-u-tables-top">{top.map((t) => tableCard(t))}</div>
          <div className="sab-u-tables-mid">
            <div className="sab-u-tables-side">{left.map((t) => tableCard(t))}</div>
            <div className="sab-style-arena-gap is-floor">Open floor</div>
            <div className="sab-u-tables-side">{right.map((t) => tableCard(t))}</div>
          </div>
        </div>
      );
    }

    if (isHeadTable) {
      const head = tables[0];
      const guests = tables.slice(1);
      const near = guests.slice(0, Math.ceil(guests.length / 2));
      const far = guests.slice(Math.ceil(guests.length / 2));
      return (
        <div className="sab-style-layout is-head-table">
          <div className="sab-style-stage is-front">Wedding Stage</div>
          <p className="sab-facing-legend mb-0">
            Head table sits below the stage with VIP seats facing the guests ·
            guest tables are Table → Seat
          </p>
          {head ? (
            <div className="sab-head-table-row">{tableCard(head, "head")}</div>
          ) : null}
          {near.length ? (
            <>
              <div className="sab-style-divider">Guest tables · near</div>
              <div
                className={`sa-table-grid sab-table-grid${
                  wideTables ? " is-wide" : ""
                }`}
                style={{ "--sab-table-cell": `${cellMin}px` }}
              >
                {near.map((t) => tableCard(t))}
              </div>
            </>
          ) : null}
          {far.length ? (
            <>
              <div className="sab-style-divider">Guest tables · farther</div>
              <div
                className={`sa-table-grid sab-table-grid${
                  wideTables ? " is-wide" : ""
                }`}
                style={{ "--sab-table-cell": `${cellMin}px` }}
              >
                {far.map((t) => tableCard(t))}
              </div>
            </>
          ) : null}
        </div>
      );
    }

    if (isCabaret) {
      return (
        <div className="sab-style-layout is-cabaret">
          <div className="sab-style-stage is-front is-soft">
            Stage / Dance floor
          </div>
          <p className="sab-facing-legend mb-0">
            Cabaret · seats around ~¾ of each table · open side faces the stage
          </p>
          <div
            className={`sa-table-grid sab-table-grid is-cabaret${
              wideTables ? " is-wide" : ""
            }`}
            style={{ "--sab-table-cell": `${cellMin}px` }}
          >
            {tables.map((t) => tableCard(t, "cabaret"))}
          </div>
        </div>
      );
    }

    if (isLong) {
      return (
        <div className="sab-style-layout is-long-table">
          <p className="sab-facing-legend mb-0">
            Family long tables · rectangular dining · Table → Seat (T1-S1…)
          </p>
          <div
            className={`sa-table-grid sab-table-grid is-long-table${
              wideTables ? " is-wide" : ""
            }`}
            style={{ "--sab-table-cell": `${cellMin}px` }}
          >
            {tables.map((t) => tableCard(t, "long"))}
          </div>
        </div>
      );
    }

    if (styleId === "courtyard") {
      return (
        <div className="sab-style-layout is-courtyard-tables">
          <div
            className={`sa-table-grid sab-table-grid${
              wideTables ? " is-wide" : ""
            }`}
            style={{ "--sab-table-cell": `${cellMin}px` }}
          >
            {tables.map((t) => tableCard(t))}
          </div>
        </div>
      );
    }

    return (
      <div
        className={`sa-table-grid sab-table-grid sab-style-layout is-banquet${
          wideTables ? " is-wide" : ""
        }`}
        style={{ "--sab-table-cell": `${cellMin}px` }}
      >
        {tables.map((t) => tableCard(t))}
      </div>
    );
  };

  return (
    <div
      className={`main home-section seat-allocation-page sab-page${
        occasionMenuOpen ? " is-picker-open" : ""
      }`}
    >
      <div className="container-fluid py-3">
        <div className="sab-toolbar">
          <h4 className="sab-page-title mb-0">Seat Allocation</h4>

          <div className="sab-toolbar-filters">
            <div
              className="sab-control-group is-inline is-occasion"
              ref={occasionMenuRef}
            >
              <span className="sab-control-label" id="sab-occasion-label">
                Event Occasion
              </span>
              <div className="sab-occasion-picker">
                <button
                  type="button"
                  id="sab-occasion"
                  className={`sab-occasion-trigger${
                    occasionMenuOpen ? " is-open" : ""
                  }`}
                  aria-haspopup="listbox"
                  aria-expanded={occasionMenuOpen}
                  aria-labelledby="sab-occasion-label"
                  onClick={() => setOccasionMenuOpen((open) => !open)}
                >
                  <span className="sab-occasion-trigger-text">
                    {occasion?.OccasionName || "Select Occasion"}
                  </span>
                  <i
                    className={`bi bi-chevron-${occasionMenuOpen ? "up" : "down"}`}
                    aria-hidden="true"
                  />
                </button>
                {occasionMenuOpen ? (
                  <ul className="sab-occasion-menu" role="listbox">
                    {!occasions.length ? (
                      <li className="sab-occasion-option is-empty">
                        No occasions
                      </li>
                    ) : (
                      occasions.map((item) => {
                        const selected = sameId(item._id, occasionId);
                        return (
                          <li key={item._id} role="presentation">
                            <button
                              type="button"
                              role="option"
                              aria-selected={selected}
                              className={`sab-occasion-option${
                                selected ? " is-selected" : ""
                              }`}
                              onClick={() => {
                                setOccasionId(String(item._id));
                                setOccasionMenuOpen(false);
                              }}
                            >
                              {item.OccasionName}
                            </button>
                          </li>
                        );
                      })
                    )}
                  </ul>
                ) : null}
              </div>
            </div>

            {/* Layout view only for classic hybrid floors — not advanced/custom styles */}
            {!useStyledFloor &&
            !seatingStyle?.isCustom &&
            allocationType === "both" &&
            occasionId ? (
              <div className="sab-control-group is-inline">
                <span className="sab-control-label">Layout view</span>
                <div className="sab-mode-tabs" role="tablist">
                  <button
                    type="button"
                    className={viewMode === "row" ? "active" : ""}
                    onClick={() => setViewMode("row")}
                  >
                    Rows
                  </button>
                  <button
                    type="button"
                    className={viewMode === "table" ? "active" : ""}
                    onClick={() => setViewMode("table")}
                  >
                    Tables
                  </button>
                  <button
                    type="button"
                    className={
                      viewMode === "both" || viewMode === "auto"
                        ? "active"
                        : ""
                    }
                    onClick={() => setViewMode("both")}
                  >
                    Both
                  </button>
                </div>
              </div>
            ) : null}

            {occasionId ? (
              <div className="sab-control-group is-inline is-layout-pick">
                <span className="sab-control-label">Seating layout</span>
                <button
                  type="button"
                  id="sab-board-layout"
                  className={`sab-layout-trigger${
                    layoutPickerOpen ? " is-open" : ""
                  }`}
                  disabled={savingLayoutPick}
                  onClick={() => setLayoutPickerOpen(true)}
                  aria-haspopup="dialog"
                  aria-expanded={layoutPickerOpen}
                >
                  <span className="sab-layout-trigger-copy">
                    <strong>{seatingStyle?.name || "Choose layout"}</strong>
                    <em>
                      {seatingStyle?.isCustom
                        ? seatingStyle?.status === "draft"
                          ? "Custom · Draft"
                          : "Custom"
                        : seatingStyle?.tag || "Advanced"}
                    </em>
                  </span>
                  <i className="bi bi-grid-3x3-gap" aria-hidden="true" />
                </button>
              </div>
            ) : null}
          </div>

          <div className="sab-toolbar-actions">
            {occasionId ? (
              <Link
                to="/seat-allocation/layouts"
                className="btn sab-manage-btn"
              >
                Manage layouts
              </Link>
            ) : null}

            {!isViewer ? (
              <button
                type="button"
                className="btn btn-primary sab-save-btn"
                disabled={!occasionId || saving}
                onClick={handleSave}
              >
                {saving ? "Saving…" : "Save Layout"}
              </button>
            ) : null}
          </div>
        </div>

        {!occasionId ? (
          <div className="sab-empty-state">
            <i className="bi bi-diagram-3" aria-hidden="true" />
            <h5>No occasion available</h5>
            <p>Create an occasion first, then return to seat guests here.</p>
          </div>
        ) : (
          <div
            className={`sab-workspace${
              useStyledFloor
                ? " is-advanced"
                : mode === "both"
                  ? " is-both"
                  : mode === "table"
                    ? " is-table"
                    : " is-row"
            }`}
          >
            <aside
              className={`sab-people-panel${
                listDropActive ? " is-drop-target" : ""
              }`}
              onDragOver={(e) => {
                if (isViewer) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = "move";
                setListDropActive(true);
              }}
              onDragLeave={() => setListDropActive(false)}
              onDrop={onDropList}
            >
              <div className="sab-people-head">
                <span className="sab-people-kicker">Available to seat</span>
                <div className="sab-people-stats">
                  <div className="sab-stat-card is-left">
                    <span className="sab-stat-value">{unseatedCount}</span>
                    <span className="sab-stat-label">Still available</span>
                  </div>
                  <div className="sab-stat-card is-seated">
                    <span className="sab-stat-value">
                      {seatedCount}
                      <em>/{totalPeople}</em>
                    </span>
                    <span className="sab-stat-label">Already seated</span>
                  </div>
                </div>
                <input
                  type="search"
                  className="form-control sab-search"
                  placeholder="Search guest…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              <div className="sab-people-legend">
                <span className="sa-legend-item is-guest">Guest</span>
                <span className="sa-legend-item is-attendee">Attendee</span>
              </div>

              <div className="sab-people-scroll">
                {filteredPeople.length === 0 ? (
                  <p className="sab-people-empty mb-0">
                    {people.length === 0
                      ? "No eligible guests for this occasion."
                      : unseatedCount === 0
                        ? "Everyone is seated. Clear a seat or drop someone here to bring them back."
                        : "No guests match your search."}
                  </p>
                ) : (
                  filteredPeople.map((group) => (
                    <div className="sab-party-group" key={group.partyId}>
                      <div className="sab-party-label">{group.partyName}</div>
                      {group.guest ? (
                        <div className="sab-section sab-section-guest">
                          <span className="sab-section-label">Guest</span>
                          {renderPersonCard(group.guest)}
                        </div>
                      ) : null}
                      {group.attendees.length > 0 ? (
                        <div className="sab-section sab-section-attendee">
                          <span className="sab-section-label">Attendees</span>
                          {group.attendees.map((att) => renderPersonCard(att))}
                        </div>
                      ) : null}
                    </div>
                  ))
                )}
              </div>
            </aside>

            {useStyledFloor ? renderCustomMapPanel() : null}
            {!useStyledFloor && (mode === "row" || mode === "both")
              ? renderMapPanel("row")
              : null}
            {!useStyledFloor && (mode === "table" || mode === "both")
              ? renderMapPanel("table")
              : null}
          </div>
        )}
      </div>

      {layoutPickerOpen ? (
        <div
          className="sab-layout-modal-overlay"
          role="presentation"
          onClick={() => setLayoutPickerOpen(false)}
        >
          <div
            className="sab-layout-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="sab-layout-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sab-layout-modal-head">
              <div>
                <span className="sab-style-picker-kicker">Advanced layouts</span>
                <strong id="sab-layout-modal-title">
                  Choose a seating layout
                </strong>
                <p className="sab-layout-modal-sub mb-0">
                  Preview and select a preset or custom floor for this occasion.
                </p>
              </div>
              <button
                type="button"
                className="sab-style-picker-close"
                aria-label="Close layout picker"
                onClick={() => setLayoutPickerOpen(false)}
              >
                ×
              </button>
            </div>

            <div className="sab-layout-modal-body">
              <div className="sab-style-grid">
                {availableStyles.map((style) => {
                  const isActive = seatingStyleId === style.id;
                  return (
                    <button
                      key={style.id}
                      type="button"
                      className={`sab-style-card${
                        isActive ? " is-active" : ""
                      }`}
                      disabled={savingLayoutPick || isViewer}
                      onClick={() =>
                        selectBoardLayout(style.id, { closePicker: true })
                      }
                    >
                      <span
                        className={`sab-style-thumb is-${
                          style.isCustom ? "custom" : style.id
                        }`}
                        aria-hidden="true"
                      >
                        <span className="sab-style-thumb-stage" />
                        <span className="sab-style-thumb-seats" />
                      </span>
                      <span className="sab-style-card-copy">
                        <span className="sab-style-card-tag">
                          {style.tag}
                          {style.status === "draft" ? " · Draft" : ""}
                          {isActive ? " · Active" : ""}
                        </span>
                        <strong>{style.name}</strong>
                        <span className="sab-style-card-blurb">
                          {style.blurb}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
              {!availableStyles.length ? (
                <p className="sab-people-empty mb-0">No layouts available.</p>
              ) : null}
            </div>

            <div className="sab-layout-modal-foot">
              <Link
                to="/seat-allocation/layouts"
                className="btn sab-manage-btn"
                onClick={() => setLayoutPickerOpen(false)}
              >
                Manage layouts
              </Link>
              <button
                type="button"
                className="btn btn-primary sab-save-btn"
                onClick={() => setLayoutPickerOpen(false)}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {renderPartyCard()}
    </div>
  );
};

export default SeatAllocationBoard;
